/**
 * ``useObjectTracksFeed`` — the viewport **backfill** for object tracks
 * (ADR-041 D-4; REQ-UI-167; REQ-QUERY-051).
 *
 * The object-track twin of :func:`map/use-tracks-feed` (the per-Producer
 * tails). On load and after each pan it fetches ``GET /api/query/object-tracks``
 * for the current bbox — every live track whose envelope intersects the
 * viewport, each with its most recent members time-ascending, **unpositioned
 * included** — and seeds the shared object-track store so lines (and the
 * Tracks panel's expanded rows) appear fully-formed, then the live SSE bridge
 * keeps them growing.
 *
 * The window is left to the BFF's default — the deployment live window the
 * config route advertises — so the backfill and the client's prune floor agree.
 *
 * # Query key + refetch policy
 *
 * As the sibling: the bbox enters the key and the URL through
 * :func:`quantizeViewport`, so a camera that settles on the same view
 * (the first-load home ``flyTo``, an inertia settle) does not re-key this
 * query on a last-digit float difference and repeat a query that costs the
 * BFF 2–8 s at the world viewport (the 2026-09-30 live pass; ADR-016
 * amendment 2026-10-01). ``refetchOnWindowFocus`` is off — the detections
 * SSE bridge grows the lines between backfills; mount / reconnect keep
 * TanStack's defaults. The rationale is written once, in
 * ``use-tracks-feed.ts`` §"Refetch policy".
 *
 * Cross-references:
 *   - REQ-UI-167 (the polyline layer); REQ-UI-181 (the live window).
 *   - REQ-QUERY-051 (the route).
 *   - map/use-tracks-feed.ts (the sibling this mirrors).
 */
import { useEffect } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import { useLiveWindowMs } from "@/chrome/use-deployment-config";
import { MAP_READER_ROLES } from "@/map/use-snapshot";
import type { SnapshotViewport } from "@/map/snapshot-types";
import { quantizeViewport } from "@/map/viewport-sync";
import type { components } from "@/lib/api-types.gen";
import { type TrackMember, useObjectTrackStore } from "@/object-tracks/object-track-store";

export type ObjectTracksResponse = components["schemas"]["ObjectTracksResponse"];
type ObjectTrackItem = components["schemas"]["ObjectTrackItem"];

/**
 * Members per track requested. Below the route's default (200; max 500) on
 * purpose: the first live pass (2026-09-30) found the world-viewport
 * backfill at 300 × 200 cost the BFF 2–8 s per call on a dev host and
 * starved every other query, and a live-window line needs far fewer
 * vertices than that to read (at 0.2 Hz detections a 10-minute track has
 * ~120 members; 60 keeps the shape).
 */
export const OBJECT_TRACKS_FEED_LIMIT_PER_TRACK = 60;

/** Tracks per response requested (the route's default is 300; its max is 500). */
export const OBJECT_TRACKS_FEED_LIMIT_TRACKS = 200;

/**
 * A pan within this window re-uses the cached response. Three times the
 * platform tails' 5 s: the live SSE bridge grows the lines between
 * backfills, so a fresh backfill is only needed when the viewport has
 * genuinely moved on, and every one of these is a heavy query.
 */
export const OBJECT_TRACKS_FEED_STALE_TIME_MS = 15_000;

/** Compose the backfill URL for a viewport (bbox :func:`quantizeViewport`-snapped). */
export function composeObjectTracksUrl(
  viewport: SnapshotViewport,
  opts: { limitPerTrack: number; limitTracks: number },
): string {
  const bbox = quantizeViewport(viewport);
  const params = new URLSearchParams({
    min_longitude: bbox.min_longitude.toString(),
    min_latitude: bbox.min_latitude.toString(),
    max_longitude: bbox.max_longitude.toString(),
    max_latitude: bbox.max_latitude.toString(),
    limit_per_track: opts.limitPerTrack.toString(),
    limit_tracks: opts.limitTracks.toString(),
  });
  return `/api/query/object-tracks?${params.toString()}`;
}

/** The TanStack query key — the quantised viewport + caps (stable; no Date). */
export function objectTracksQueryKey(
  viewport: SnapshotViewport,
  limitPerTrack: number,
  limitTracks: number,
): readonly unknown[] {
  const bbox = quantizeViewport(viewport);
  return [
    "object-tracks",
    bbox.min_longitude,
    bbox.min_latitude,
    bbox.max_longitude,
    bbox.max_latitude,
    limitPerTrack,
    limitTracks,
  ];
}

export interface UseObjectTracksFeedOptions {
  /** Bounding box to backfill; ``null`` disables the query until the map has loaded. */
  viewport: SnapshotViewport | null;
  /** Override the per-track member cap. Production omits. */
  limitPerTrack?: number;
  /** Override the per-response track cap. Production omits. */
  limitTracks?: number;
  /** Override the stale time. Tests set 0; production omits. */
  staleTimeMs?: number;
}

function membersFromApi(item: ObjectTrackItem): readonly TrackMember[] {
  return item.members.map((m) => ({
    report_id: m.report_id,
    time: m.time,
    latitude_deg: m.latitude_deg,
    longitude_deg: m.longitude_deg,
    elevation_m: m.elevation_m,
    primary_class: m.primary_class,
    detection_confidence: m.detection_confidence,
    state: m.state,
  }));
}

export function useObjectTracksFeed(
  options: UseObjectTracksFeedOptions,
): UseQueryResult<ObjectTracksResponse, ApiError> {
  const session = useSession();
  const hasReadRole = session.hasAnyRole(MAP_READER_ROLES);
  const limitPerTrack = options.limitPerTrack ?? OBJECT_TRACKS_FEED_LIMIT_PER_TRACK;
  const limitTracks = options.limitTracks ?? OBJECT_TRACKS_FEED_LIMIT_TRACKS;
  const retentionMs = useLiveWindowMs();
  const { viewport } = options;
  const enabled = viewport !== null && hasReadRole;

  const query = useQuery<ObjectTracksResponse, ApiError>({
    queryKey:
      viewport !== null
        ? objectTracksQueryKey(viewport, limitPerTrack, limitTracks)
        : ["object-tracks", "disabled"],
    queryFn: async ({ signal }) => {
      if (viewport === null) {
        throw new ApiError(0, null, "useObjectTracksFeed: query ran without viewport");
      }
      const url = composeObjectTracksUrl(viewport, { limitPerTrack, limitTracks });
      const raw = await apiFetch<unknown>(url, { signal });
      if (!isObjectTracksResponse(raw)) {
        console.error(
          "[meridian/map/use-object-tracks-feed] object-tracks response failed shape validation",
          raw,
        );
        throw new ApiError(500, raw, "object-tracks response did not match the expected shape");
      }
      return raw;
    },
    enabled,
    staleTime: options.staleTimeMs ?? OBJECT_TRACKS_FEED_STALE_TIME_MS,
    // The detections SSE bridge grows the lines between backfills; see the
    // module docstring §"Query key + refetch policy".
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Seed the shared store on each distinct response; ``seed`` unions the
  // backfilled members with any live-appended ones per track.
  const data = query.data;
  useEffect(() => {
    if (data !== undefined) {
      const store = useObjectTrackStore.getState();
      for (const track of data.tracks) {
        const { summary } = track;
        store.seed(
          {
            track_id: summary.track_id,
            producer_id: summary.producer_id,
            object_id: summary.object_id,
          },
          summary,
          membersFromApi(track),
          { retentionMs },
        );
      }
    }
  }, [data, retentionMs]);

  return query;
}

function isObjectTracksResponse(value: unknown): value is ObjectTracksResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    Array.isArray(obj.tracks) &&
    typeof obj.truncated === "boolean" &&
    typeof obj.duration_ms === "number"
  );
}
